/*
 * Copyright 2026 Sony Corporation
 */

import type { Device, GatewaySummary, World } from '@ips-device-verse/twin-service/schema';

import { useAuth } from '../contexts/auth-context';
import { useAsync, type AsyncState } from './use-async';

export const useWorlds = (): AsyncState<World[]> => {
  const { api } = useAuth();
  return useAsync(() => api.listWorlds(), [api]);
};

export const useWorld = (worldId: string): AsyncState<{ world: World; devices: Device[] }> => {
  const { api, email } = useAuth();
  return useAsync(
    async () => {
      const [{ world }, devices] = await Promise.all([api.getWorld(worldId), api.listDevices(worldId)]);
      return { world, devices };
    },
    [api, worldId],
    // Per user, so a sign-out cannot show the next one what the last one saw.
    `${email}|world:${worldId}`
  );
};

/**
 * Just the name, for pages that only want to say which world they are in.
 *
 * `known` is for the page that has already loaded the world: passing it skips
 * the request entirely, so a shared header costs a fetch only where the page
 * would otherwise have none.
 */
export const useWorldName = (worldId: string, known?: string): string | undefined => {
  const { api, email } = useAuth();
  const { data } = useAsync(
    async () => known ?? (await api.getWorld(worldId)).world.name,
    [api, worldId, known],
    `${email}|world-name:${worldId}`
  );
  return data;
};

/** Registered gateways, which is how a freshly opened tab knows one exists at all. */
export const useGateways = (worldId: string): AsyncState<GatewaySummary[]> => {
  const { api, email } = useAuth();
  return useAsync(() => api.listGateways(worldId), [api, worldId], `${email}|gateways:${worldId}`);
};
